import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle2, Lock, Loader2, Building2, ShoppingBag, CreditCard, Coins, KeyRound,
} from 'lucide-react';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import { formatApiDetail, parseResponseJson } from '../lib/http';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '') ? process.env.REACT_APP_BACKEND_URL : '';

const PLAN_GRADIENTS = {
  basic: 'from-emerald-500 to-teal-500',
  starter: 'from-blue-500 to-indigo-500',
  growth: 'from-violet-500 to-purple-600',
};

const COST_BADGE = {
  plan: { label: 'Plan', className: 'bg-sky-500/15 text-sky-300' },
  credits: { label: 'Credits', className: 'bg-amber-500/15 text-amber-300' },
  byok: { label: 'BYOK', className: 'bg-emerald-500/15 text-emerald-300' },
  mix: { label: 'Mix', className: 'bg-violet-500/15 text-violet-300' },
};

const GROUP_ORDER = [
  'Build', 'Own', 'Operate', 'Marketing Suite', 'Grow', 'AI', 'AI Tools',
  'Support', 'Premium Add-ons', 'AI Tools Add-ons',
];

function CostBadge({ model }) {
  const meta = COST_BADGE[model] || COST_BADGE.plan;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

export default function SnagcartApps() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [planKey, setPlanKey] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [activatedModules, setActivatedModules] = useState([]);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [allModulesByGroup, setAllModulesByGroup] = useState({});
  const [includedCredits, setIncludedCredits] = useState({});
  const [paymentModel, setPaymentModel] = useState(null);
  const [pricing, setPricing] = useState({});
  const [billingMode, setBillingMode] = useState('mock');
  const [region, setRegion] = useState('us');
  const [provider, setProvider] = useState('stripe');
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [busy, setBusy] = useState('');
  const [mockSession, setMockSession] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showLocked, setShowLocked] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [modRes, catalogRes] = await Promise.all([
        fetch(`${API_URL}/api/snagcart/activated-modules`, { headers }),
        fetch(`${API_URL}/api/subscription/catalog`),
      ]);
      if (!modRes.ok) throw new Error('Failed to load modules');
      const data = await modRes.json();
      const catalog = catalogRes.ok ? await catalogRes.json() : {};
      setPlanKey(data.plan);
      setPlanName(data.plan_name);
      setActivatedModules(data.modules || []);
      setAvailableAddons(data.available_addons || []);
      setIncludedCredits(data.included_credits || catalog.included_credits?.[data.plan] || {});
      // Prefer entitlements full catalog; fall back to catalog payload
      const byGroup = data.all_modules_by_group && Object.keys(data.all_modules_by_group).length
        ? data.all_modules_by_group
        : (catalog.all_modules_by_group || {});
      // Merge active flags from all_modules_list when present
      const activeMap = {};
      (data.all_modules_list || []).forEach((m) => { activeMap[m.id] = m; });
      const merged = {};
      Object.entries(byGroup).forEach(([group, mods]) => {
        merged[group] = (mods || []).map((m) => {
          const live = activeMap[m.id];
          return live ? { ...m, ...live } : {
            ...m,
            active: (data.modules || []).some((a) => a.id === m.id)
              || (data.available_addons || []).some((a) => a.id === m.id && a.owned),
          };
        });
      });
      setAllModulesByGroup(merged);
      setPricing(catalog.pricing || {});
      setBillingMode(catalog.billing_mode || 'live');
      setPaymentModel(catalog.payment_model || null);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runAddonCheckout = async (addon) => {
    setBusy(addon.id);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const origin = window.location.origin;
      const res = await fetch(`${API_URL}/api/subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          addon_id: addon.id,
          region,
          provider: 'stripe',
          success_url: `${origin}/snagcart/apps?payment=stripe&session_id={SESSION_ID}`,
          cancel_url: `${origin}/snagcart/apps?payment=cancelled`,
        }),
      });
      const { data } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, 'Checkout failed'));
      if (data.success) { setMessage(data.message); await load(); return; }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data.redirect_url && data.mode === 'live') { window.location.href = data.redirect_url; return; }
      if (data.mode === 'mock') { setMockSession(data); return; }
      throw new Error(formatApiDetail(data, 'No checkout URL returned'));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
      setPaymentTarget(null);
    }
  };

  const confirmMock = async () => {
    if (!mockSession?.session_id) return;
    setBusy('mock');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/subscription/checkout/${encodeURIComponent(mockSession.session_id)}/confirm-mock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await parseResponseJson(res);
      if (!res.ok) throw new Error(formatApiDetail(data, 'Failed'));
      setMessage(data.message || 'Add-on activated!');
      setMockSession(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    if (sessionId && payment && payment !== 'cancelled') {
      (async () => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/subscription/checkout/${encodeURIComponent(sessionId)}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { data } = await parseResponseJson(res);
        if (data.success || data.payment_status === 'paid') {
          setMessage(data.message || 'Add-on activated!');
        }
        setSearchParams({});
        await load();
      })();
    }
  }, [searchParams, setSearchParams]);

  const buyAddon = (addon) => {
    setPaymentTarget({
      type: 'addon',
      addonId: addon.id,
      title: addon.name,
      subtitle: addon.desc,
      priceUsd: addon.price_usd,
      priceInr: addon.price_inr,
      amountLabel: region === 'in' ? `₹${addon.price_inr}` : `$${addon.price_usd}`,
    });
  };

  const unpurchasedAddons = availableAddons.filter((a) => !a.owned);
  const orderedGroups = [
    ...GROUP_ORDER.filter((g) => allModulesByGroup[g]),
    ...Object.keys(allModulesByGroup).filter((g) => !GROUP_ORDER.includes(g)),
  ];

  const totalCatalog = Object.values(allModulesByGroup).reduce((n, mods) => n + (mods?.length || 0), 0);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <div className="border-b border-white/10 bg-[#0d0d14] px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="h-4 w-px bg-white/10" />
        <Building2 size={16} className="text-orange-400" />
        <span className="font-semibold text-sm">Snagcart Modules</span>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {planKey ? (
          <div className={`mb-8 rounded-2xl border border-white/10 bg-gradient-to-r ${PLAN_GRADIENTS[planKey] || 'from-zinc-700 to-zinc-800'} p-6 flex flex-wrap items-center justify-between gap-4`}>
            <div>
              <p className="text-white/80 text-sm">Active plan</p>
              <h2 className="text-2xl font-bold">{planName}</h2>
              <p className="text-white/70 text-sm mt-1">
                {activatedModules.length} active · {totalCatalog} modules in catalog
              </p>
              {(includedCredits.lead_credits || includedCredits.ai_studio_credits) && (
                <p className="text-white/80 text-xs mt-2 flex items-center gap-2">
                  <Coins size={12} />
                  Plan includes {includedCredits.lead_credits || 0} lead credits · {includedCredits.ai_studio_credits || 0} AI credits
                </p>
              )}
            </div>
            <button onClick={() => navigate('/subscription-plans')} className="rounded-xl bg-white/20 px-5 py-2.5 text-sm font-semibold hover:bg-white/30">
              {planKey === 'growth' ? 'Manage plan' : 'Upgrade plan'}
            </button>
          </div>
        ) : (
          <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 flex justify-between items-center">
            <p className="text-zinc-300">Choose a plan to unlock platform modules.</p>
            <button onClick={() => navigate('/subscription-plans')} className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-black">Choose plan</button>
          </div>
        )}

        {paymentModel?.layers?.length > 0 && (
          <div className="mb-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {paymentModel.layers.map((layer) => (
              <div key={layer.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-sm font-semibold text-white">{layer.title}</p>
                <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{layer.desc}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mb-6 flex flex-wrap items-center gap-3 text-xs text-zinc-400">
          <span className="flex items-center gap-1"><CostBadge model="plan" /> Software unlock</span>
          <span className="flex items-center gap-1"><CostBadge model="credits" /> Platform credits</span>
          <span className="flex items-center gap-1"><CostBadge model="byok" /> Your API keys</span>
          <span className="flex items-center gap-1"><CostBadge model="mix" /> Plan + credits/BYOK</span>
          <label className="ml-auto flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={showLocked} onChange={(e) => setShowLocked(e.target.checked)} />
            Show locked modules
          </label>
        </div>

        {message && <div className="mb-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-200">{message}</div>}
        {error && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300">{error}</div>}
        {mockSession && (
          <div className="mb-4 rounded-xl border border-sky-400/30 bg-sky-400/10 p-4">
            <p className="text-sm">Confirm mock payment for add-on</p>
            <button type="button" onClick={confirmMock} disabled={busy === 'mock'} className="mt-2 rounded-lg bg-sky-400 px-3 py-1.5 text-sm font-semibold text-black">
              Confirm payment
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={32} /></div>
        ) : (
          <>
            <section className="mb-10">
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-4">
                <CheckCircle2 size={16} className="text-emerald-400" /> All modules & submodules ({totalCatalog})
              </h2>
              {orderedGroups.length > 0 ? (
                orderedGroups.map((group) => {
                  const mods = (allModulesByGroup[group] || []).filter((m) => showLocked || m.active || m.kind === 'addon');
                  if (!mods.length) return null;
                  return (
                    <div key={group} className="mb-6">
                      <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{group}</p>
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {mods.map((mod) => {
                          const isAddon = mod.kind === 'addon';
                          const isActive = Boolean(mod.active) || (isAddon && availableAddons.some((a) => a.id === mod.id && a.owned));
                          const locked = !isActive;
                          const openPath = mod.nav_path || null;
                          return (
                            <div
                              key={mod.id}
                              className={`rounded-xl border p-4 ${
                                isActive
                                  ? 'border-emerald-500/20 bg-emerald-500/5'
                                  : 'border-white/10 bg-white/[0.02] opacity-80'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-white truncate">{mod.name}</p>
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    <CostBadge model={mod.cost_model || (isAddon ? 'plan' : 'plan')} />
                                    {isAddon && (
                                      <span className="rounded-full bg-violet-500/20 px-2 py-0.5 text-[10px] text-violet-300">Add-on</span>
                                    )}
                                  </div>
                                </div>
                                {locked ? <Lock size={14} className="text-zinc-600 flex-shrink-0" /> : <CheckCircle2 size={14} className="text-emerald-400 flex-shrink-0" />}
                              </div>
                              <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{mod.desc}</p>
                              {mod.cost_note && (
                                <p className="text-[10px] text-zinc-600 mt-2 line-clamp-2 flex gap-1">
                                  {mod.cost_model === 'byok' ? <KeyRound size={10} className="mt-0.5 flex-shrink-0" /> : <Coins size={10} className="mt-0.5 flex-shrink-0" />}
                                  {mod.cost_note}
                                </p>
                              )}
                              <div className="mt-3 flex items-center justify-between gap-2">
                                <span className={`text-[10px] ${isActive ? 'text-emerald-400' : 'text-zinc-500'}`}>
                                  {isActive ? 'Active' : isAddon ? 'Purchase add-on' : 'Upgrade plan'}
                                </span>
                                {isActive && openPath && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(openPath)}
                                    className="text-[10px] font-semibold text-emerald-300 hover:text-emerald-200"
                                    data-testid={`open-module-${mod.id}`}
                                  >
                                    Open
                                  </button>
                                )}
                                {locked && isAddon && (
                                  <button
                                    type="button"
                                    onClick={() => buyAddon(mod)}
                                    className="text-[10px] font-semibold text-violet-300 hover:text-violet-200"
                                  >
                                    ${mod.price_usd}/mo
                                  </button>
                                )}
                                {locked && !isAddon && (
                                  <button
                                    type="button"
                                    onClick={() => navigate('/subscription-plans')}
                                    className="text-[10px] font-semibold text-sky-300 hover:text-sky-200"
                                  >
                                    Choose plan
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-500 text-sm">No modules in catalog yet.</p>
              )}
            </section>

            <section>
              <h2 className="text-sm font-semibold flex items-center gap-2 mb-2">
                <ShoppingBag size={16} className="text-violet-400" /> Add-on modules
              </h2>
              <p className="text-xs text-zinc-500 mb-4">Premium capabilities sold separately — pay with Stripe.</p>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {unpurchasedAddons.map((addon) => (
                  <div key={addon.id} className="rounded-xl border border-white/10 bg-white/[0.03] p-5 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-white">{addon.name}</p>
                      <Lock size={14} className="text-zinc-600 flex-shrink-0" />
                    </div>
                    <p className="mt-2 text-xs text-zinc-500 flex-1">{addon.desc}</p>
                    <div className="mt-2"><CostBadge model={addon.cost_model || 'plan'} /></div>
                    <p className="mt-3 text-sm text-emerald-300">${addon.price_usd}/mo · ₹{addon.price_inr}/mo</p>
                    <button
                      type="button"
                      disabled={busy === addon.id}
                      onClick={() => buyAddon(addon)}
                      className="mt-4 flex items-center justify-center gap-2 rounded-lg bg-violet-500 py-2 text-xs font-semibold hover:bg-violet-400 disabled:opacity-60"
                    >
                      <CreditCard size={14} /> Buy add-on
                    </button>
                  </div>
                ))}
                {unpurchasedAddons.length === 0 && (
                  <p className="text-sm text-zinc-500 col-span-full">All add-ons owned, or none available.</p>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      <SubscriptionPaymentModal
        open={Boolean(paymentTarget)}
        onClose={() => setPaymentTarget(null)}
        target={paymentTarget}
        region={region}
        setRegion={setRegion}
        provider={provider}
        setProvider={setProvider}
        pricing={pricing}
        billingMode={billingMode}
        onConfirm={() => paymentTarget && runAddonCheckout(availableAddons.find((a) => a.id === paymentTarget.addonId) || { id: paymentTarget.addonId })}
        busy={Boolean(busy)}
      />
    </div>
  );
}
